import { format, isToday, isTomorrow } from 'date-fns';
import { useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import {
  Button,
  Carousel,
  CarouselPaging,
  CarouselRef,
  IconArrowBack,
  IconArrowForward,
  Text,
  tokens,
} from '@fhs/ui';

import { IDatePickerData } from './types';

interface DayPickerWebProps {
  datePickerData: IDatePickerData[];
  selectedDay: number;
  setSelectedDay: React.Dispatch<React.SetStateAction<number>>;
}

export default function DayPicker({
  datePickerData,
  selectedDay,
  setSelectedDay,
}: DayPickerWebProps) {
  const carouselRef = useRef<CarouselRef>(null);
  const [carouselPaging, setCarouselPaging] = useState<CarouselPaging | null>(null);

  const nextPage = () => {
    carouselRef.current?.nextPage();
  };

  const prevPage = () => {
    carouselRef.current?.prevPage();
  };

  const isNextDisabled = carouselPaging
    ? carouselPaging.currentPage >= carouselPaging.totalPages - 1
    : true;
  const isPrevDisabled = carouselPaging ? carouselPaging.currentPage <= 0 : true;

  const hideButtons = !carouselPaging || carouselPaging.totalPages <= 1;

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text.Heading type="three">Select Date</Text.Heading>
        {!hideButtons && (
          <View style={styles.navigationButtons}>
            <Button
              style={styles.navButton}
              type="ghost"
              size="sm"
              onPress={prevPage}
              disabled={isPrevDisabled}
              disabledStyle={styles.navButtonDisabled}
            >
              <Button.Icon color={isPrevDisabled ? tokens.colors.$blackOpacity10 : undefined}>
                <IconArrowBack />
              </Button.Icon>
            </Button>

            <Button
              style={styles.navButton}
              type="ghost"
              size="sm"
              onPress={nextPage}
              disabled={isNextDisabled}
              disabledStyle={styles.navButtonDisabled}
            >
              <Button.Icon color={isNextDisabled ? tokens.colors.$blackOpacity10 : undefined}>
                <IconArrowForward />
              </Button.Icon>
            </Button>
          </View>
        )}
      </View>

      <View style={styles.carouselContainer}>
        <Carousel
          ref={carouselRef}
          data={datePickerData}
          renderItem={({ item, index }) => (
            <Pressable
              key={item.date}
              aria-disabled={item.isUnavailable}
              disabled={item.isUnavailable}
              style={[
                styles.dayButton,
                selectedDay === index && styles.selectedDay,
                item.isUnavailable && styles.disabledDay,
              ]}
              onPress={() => setSelectedDay(index)}
            >
              <Text.Ui
                size="lg"
                style={[
                  styles.dayOfWeekText,
                  selectedDay === index
                    ? styles.selectedFormattedRelativeDay
                    : styles.formattedRelativeDay,
                  item.isUnavailable && styles.disabledText,
                ]}
                numberOfLines={1}
              >
                {formatRelativeDay(item.date)}
              </Text.Ui>
              <Text.Ui
                size="md"
                style={[styles.dateText, item.isUnavailable && styles.disabledText]}
              >
                {item.isUnavailable ? 'Closed' : format(new Date(item.date + 'T12:00:00'), 'MMM d')}
              </Text.Ui>
            </Pressable>
          )}
          itemWidth={112} // 104px width + 8px gap
          onChangePage={setCarouselPaging}
        />
      </View>
    </View>
  );
}

function formatRelativeDay(date: string): string {
  const dateObj = new Date(date + 'T12:00:00');

  if (isToday(dateObj)) {
    return 'Today';
  }

  if (isTomorrow(dateObj)) {
    return 'Tomorrow';
  }

  return format(dateObj, 'EEE');
}

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 28,
    marginBottom: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  navigationButtons: {
    flexDirection: 'row',
    gap: 16,
  },
  navButton: {
    borderColor: tokens.colors.$blackOpacity10,
    justifyContent: 'space-around',
  },
  navButtonDisabled: {
    borderColor: tokens.colors.$blackOpacity04,
  },
  carouselContainer: {
    width: '100%',
  },
  dayButton: {
    width: 104,
    gap: 4,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.30)',
    borderRadius: 8,
    paddingVertical: 12,
    paddingLeft: 16,
    paddingRight: 8,
    flexShrink: 0,
  },
  selectedDay: {
    backgroundColor: '#FFEDD5',
    borderColor: '#D7A748',
  },
  disabledDay: {
    backgroundColor: tokens.colors.$blackOpacity04,
    borderColor: tokens.colors.$blackOpacity30,
  },
  disabledText: {
    color: tokens.colors.$disabledText,
  },
  formattedRelativeDay: {
    color: tokens.colors.$placeholderText,
  },
  selectedFormattedRelativeDay: {
    color: tokens.colors.$houseDark,
  },
  dayOfWeekText: {
    fontSize: 16,
  },
  dateText: {
    fontSize: 20,
    fontWeight: '400',
  },
});
